import zoomSdk from '@zoom/appssdk';
import { apiPost } from './api';

type AuthContext = {
  orgId: string;
  codeVerifier: string;
  mode: 'admin' | 'patient';
  patientId: string | null;
  tempPatientId: string | null;
};

let authContext: AuthContext | null = null;
let activeOrgId: string | null = null;

const NON_ORG_PATH_PREFIXES = new Set(['login', 'organizations', 'error', 'dashboard', 'connect', 'api']);

const ZOOM_CAPABILITIES = [
  "getRunningContext",
  "authorize",
  "onAuthorized",
  // zoom contact center
  "getEngagementVariableValue",
  "getEngagementContext",
  // zoom phone
  "getPhoneContext",
  "onPhoneCalleeAnswered",
] as const;

const getOrgIdFromPath = (): string | null => {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (!first || NON_ORG_PATH_PREFIXES.has(first)) {
    return null;
  }
  return first;
};

const getActiveOrgId = (): string | null => activeOrgId ?? getOrgIdFromPath();

const syncOrgContextFromUrl = () => {
  const orgId = getOrgIdFromPath();
  if (orgId) {
    activeOrgId = orgId;
  }
};

const attachZoomListeners = () => {
  zoomSdk.removeEventListener?.('onAuthorized', handleAuthorizedEvent);
  zoomSdk.removeEventListener?.('onPhoneCalleeAnswered', handlePhoneCalleeAnsweredEvent);
  zoomSdk.addEventListener('onAuthorized', handleAuthorizedEvent);
  zoomSdk.addEventListener('onPhoneCalleeAnswered', handlePhoneCalleeAnsweredEvent);
};

const initZoomApp = async () => {
  try {
    await zoomSdk.config({ capabilities: [...ZOOM_CAPABILITIES] });
    attachZoomListeners();
  } catch (_error) {}
};

const onRouteChange = () => {
  syncOrgContextFromUrl();
  initZoomApp();
};

export const isInZoomClient = async (): Promise<string | null> => {
  try {
    await initZoomApp();
    const ctx = await zoomSdk.getRunningContext();
    return ctx?.context;
  } catch {
    return null;
  }
};

export const triggerInClientAuth = async (orgId: string, mode: 'admin' | 'patient') => {
  try {
    const initZoomInClient = await isInZoomClient();
    if (!initZoomInClient) {
      throw new Error('Please open this page in the Zoom Desktop Client Application.');
    }

    const verifier = generatePKCE(64);
    let patientId = await getEngagementVariableValue('PatientDashboard.PatientID');
    let tempPatientId = await getEngagementVariableValue('PatientDashboard.Autofill-PatientID');

    if (patientId) patientId = patientId.trim();
    if (tempPatientId) tempPatientId = tempPatientId.trim();

    activeOrgId = orgId;
    authContext = {
      orgId,
      codeVerifier: verifier,
      mode,
      patientId,
      tempPatientId,
    };

    await zoomSdk.authorize({
      codeChallenge: verifier,
    });
  } catch (error) {
    console.error('Authorization call failed:', error);
    throw error;
  }
}

const generatePKCE = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};

const getEngagementVariableValue = async (variableName: string): Promise<string | null> => {
  try {
    await initZoomApp();
    const ctx = await zoomSdk.getEngagementContext();
    if (!ctx) {
      return null;
    }
    const engagementId = ctx.engagementContext.engagementId;
    const value = await zoomSdk.getEngagementVariableValue({ engagementId });
    return value.variables.find((v: any) => v.name === variableName)?.value;
  } catch (error) {
    console.log('Error getting engagement variable value:', error);
    return null;
  }
}

const attemptGettingZoomPhoneRedirectPath = async (orgId: string): Promise<string | null> => {
  const initZoomInClient = await isInZoomClient();
  if (initZoomInClient !== 'inPhone') {
    return null;
  }
  const phoneContext = await zoomSdk.getPhoneContext();
  if (!phoneContext || !phoneContext.callObject) {
    return null;
  }
  const phoneNumber = phoneContext.callObject.caller.phoneNumber;
  const response = await apiPost(`/api/${orgId}/ehr/zoom-phone-number`, { phone: phoneNumber });
  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  let redirectPath = '';
  if (data?.patientId) {
    redirectPath = `/${orgId}/dashboard/${data.patientId}`;
  } else if (data?.tempPatientId) {
    redirectPath = `/${orgId}/patient_dashboard?autofill=${data.tempPatientId}`;
  }
  return redirectPath;
}

/* EVENT HANDLERS */
const handleAuthorizedEvent = async (event: { code: string }) => {
  if (!authContext) {
    return;
  }
  try {
    const { code } = event;
    const { orgId, codeVerifier, mode, patientId, tempPatientId } = authContext;

    const zoomPhoneRedirectPath = await attemptGettingZoomPhoneRedirectPath(orgId);
    if (zoomPhoneRedirectPath) {
      await zoomRedirect(zoomPhoneRedirectPath);
      return;
    }

    const response = await apiPost(`/api/${orgId}/oauth/zoom/in-client`, {
      code,
      code_verifier: codeVerifier,
      type: mode,
      redirect_url: window.location.href,
      patient_id: patientId,
      temp_patient_id: tempPatientId,
    });
    if (!response.ok) {
      throw new Error('Failed to complete Zoom sign-in. Please try again.');
    }

    const data = await response.json();
    window.location.href = data.redirectPath;
  } catch (error) {
    console.error('Zoom in-client sign-in failed:', JSON.stringify(error, null, 2));
  }
};

const handlePhoneCalleeAnsweredEvent = async () => {
  const orgId = getActiveOrgId();
  if (!orgId) {
    return;
  }
  try {
    const zoomPhoneRedirectPath = await attemptGettingZoomPhoneRedirectPath(orgId);
    if (zoomPhoneRedirectPath) {
      await zoomRedirect(zoomPhoneRedirectPath);
    }
  } catch (error) {
    console.error('Phone callee answered event failed:', JSON.stringify(error, null, 2));
  }
}

const zoomRedirect = async (path: string) => {
  window.history.pushState({}, '', path);
  syncOrgContextFromUrl();
  window.dispatchEvent(new Event('navigate'));
  await initZoomApp();
}

initZoomApp();
syncOrgContextFromUrl();
window.addEventListener('navigate', onRouteChange);
window.addEventListener('popstate', onRouteChange);